import { defaultMemoize } from 'reselect';
import { getIsMobile } from '@wix/da-shared-react/pkg/publicSession/selectors';
import { withOffset, selectors } from '@wix/da-shared-react/pkg/Stream';
import { getDeviationsByIds } from '@wix/da-shared-react/pkg/redux/deviations/selectors';
import { AppState } from '../types/store';
import { getFacetRequestQuery } from './facet';
import { getFacetStreamIdByPageAndRawPageType } from '../../utils';
import { getPage } from './page';
import { Pages, DEFAULT_PAGE_SIZE, PAGE_STREAM_ID } from '../../constants';
import { FacetPageType } from '../../types/api';
import { FacetStreamItems } from '../components/FacetStream';
import { getIsNetworkBarPage } from './app';
import { flagEnabled } from '@wix/da-shared-react/pkg/redux/flags';

export function getCurrentFacetStreamId(state: AppState) {
  const page = getPage(state);

  if (getIsNetworkBarPage(state)) {
    return PAGE_STREAM_ID;
  }

  const requestParams = getFacetRequestQuery(state);
  return getFacetStreamIdByPageAndRawPageType(page, requestParams.page_type);
}

export function getFacetStreamState(state: AppState, streamId: string) {
  return {
    offset: withOffset.selectors.getNextOffset(state, streamId),
    facetRequest: getFacetRequestQuery(state),
  };
}

export function getItemsPerFetch(
  state: AppState,
  facetPageType?: FacetPageType
) {
  const pageSize = getPageSize(state);

  if (facetPageType) {
    if (facetPageType === 'dailydeviations') {
      return 3;
    }
    return getIsMobile(state) ? 10 : pageSize;
  }

  const page = getPage(state);
  if (page === Pages.DailyDeviation) {
    return 3;
  }
  return getIsMobile(state) ? 10 : pageSize;
}

export function getIsCurrentStreamEmpty(state: AppState) {
  const streamId = getCurrentFacetStreamId(state);
  const items = selectors.getItems(state, streamId);
  return !items || items.length === 0;
}

export const getStreamItems = defaultMemoize(
  (state: AppState, streamId: string) => {
    const items = selectors.getItems(state, streamId);
    const schema = selectors.getStreamParam(state, streamId, 'itemType');

    if (schema === 'deviation') {
      return getDeviationsByIds(state, items);
    }

    // so far only deviations are normalized, but this would be the place
    // to denormalize everything else as well

    return items as FacetStreamItems;
  }
);

export function getHasMore(state: AppState) {
  const streamId = getCurrentFacetStreamId(state);
  return selectors.hasMore(state, streamId);
}

export function getTotal(state: AppState) {
  const streamId = getCurrentFacetStreamId(state);
  return selectors.getTotal(state, streamId);
}

export function getStreamError(state: AppState) {
  const streamId = getCurrentFacetStreamId(state);
  return selectors.getErrorMsg(state, streamId);
}

export function getInitialOffset(state: AppState): number {
  const streamId = getCurrentFacetStreamId(state);
  return selectors.getStreamParam(state, streamId, 'initialOffset') || 0;
}

export function getPageSize(state: AppState): number {
  const streamId = getCurrentFacetStreamId(state);
  return (
    selectors.getStreamParam(state, streamId, 'pageSize') || DEFAULT_PAGE_SIZE
  );
}

export function getCurrentPage(state: AppState): number {
  const streamId = getCurrentFacetStreamId(state);
  return selectors.getStreamParam(state, streamId, 'currentPage');
}

export function getTotalPages(state: AppState): number | undefined {
  const streamId = getCurrentFacetStreamId(state);
  return selectors.getStreamParam(state, streamId, 'totalPages');
}

export function getCaughtUpIndex(state: AppState): number | undefined {
  if (!flagEnabled(state, 'new_content_separator')) {
    return;
  }

  const streamId = getCurrentFacetStreamId(state);
  const index = selectors.getStreamParam(state, streamId, 'caughtUpIndex');

  if (!index) {
    return;
  }

  if (index === 'infinity') {
    return Number.POSITIVE_INFINITY;
  }

  return Number(index);
}
